import React, { useState, useCallback, useEffect, useRef } from 'react';
import EnhancedFilePreview from './EnhancedFilePreview';
import { motion, AnimatePresence } from 'framer-motion';
import FileBox from './FileBox';
import ActionMenu from './ActionMenu';
import TrainingProgressDisplay from './TrainingProgressDisplay';
import TrainingConfigWindow, { TrainingConfig } from './TrainingConfigWindow';
import { FileData, ActionMenuState } from '../../types/training';
import { FaRobot, FaCloudDownloadAlt } from 'react-icons/fa';
import { useTrainingSocket } from '../../context/TrainingSocketContext';
import { BoxData } from '../../services/trainingSocketService';
import { loadTestFiles, loadMnistFiles, fileDataToFileInfo } from '../../utils/fileLoaderUtil';
import { socketService } from '../../services/trainingSocketService';
import OverwriteProjectDialog from './OverwriteProjectDialog';
import { fileDataToFile } from '../../utils/fileUploadUtils';
import s3UploadService from '../../services/s3UploadService';
import supabase from '../../services/supabaseClient';
import './TrainingApp.css';

const rowVariants = {
  initial: { y: 0, opacity: 1 },
  slideDown: { y: '85%', opacity: 0.9, scale: 0.8, x: '0%' },
  slideUp: { y: '-100%', opacity: 0 },
  enter: { y: 0, opacity: 1 },
};

const TrainingApp: React.FC = () => {
  const [stage, setStage] = useState<'initial' | 'exampleSelection' | 'config' | 'trainingInProgress'>('initial');
  const [inputFiles, setInputFiles] = useState<FileData[]>([]);
  const [outputFiles, setOutputFiles] = useState<FileData[]>([]);
  const [inputExampleFiles, setInputExampleFiles] = useState<FileData[]>([]);
  const [outputExampleFiles, setOutputExampleFiles] = useState<FileData[]>([]);
  const [actionMenu, setActionMenu] = useState<ActionMenuState>({ visible: false, x: 0, y: 0, file: null, boxId: null });
  const [previewFile, setPreviewFile] = useState<FileData | null>(null);
  const [previewBoxId, setPreviewBoxId] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [trainingConfig, setTrainingConfig] = useState<TrainingConfig | null>(null);
  const [overwriteDialog, setOverwriteDialog] = useState<{
    show: boolean;
    projectName: string;
    message: string;
    originalData: any;
  }>({
    show: false,
    projectName: '',
    message: '',
    originalData: null,
  });

  const { isConnected, processBoxData, startTraining } = useTrainingSocket();

  const handleFilesAdd = useCallback((boxId: string, newFiles: FileData[]) => {
    switch (boxId) {
      case 'inputFiles':
        setInputFiles((prev) => [...prev, ...newFiles]);
        break;
      case 'outputFiles':
        setOutputFiles((prev) => [...prev, ...newFiles]);
        break;
      case 'inputExample':
        setInputExampleFiles((prev) => [...prev, ...newFiles]);
        break;
      case 'outputExample':
        setOutputExampleFiles((prev) => [...prev, ...newFiles]);
        break;
      default:
        console.warn(`Unknown boxId in handleFilesAdd: ${boxId}`);
    }
  }, []);

  const removeFile = (fileId: string, boxId: string) => {
    switch (boxId) {
      case 'inputFiles': setInputFiles(prev => prev.filter(f => f.id !== fileId)); break;
      case 'outputFiles': setOutputFiles(prev => prev.filter(f => f.id !== fileId)); break;
      case 'inputExample': setInputExampleFiles(prev => prev.filter(f => f.id !== fileId)); break;
      case 'outputExample': setOutputExampleFiles(prev => prev.filter(f => f.id !== fileId)); break;
    }
  };

  const handleFileUpdate = useCallback((boxId: string, updatedFile: FileData) => {
    switch (boxId) {
      case 'inputFiles':
        setInputFiles(prev => prev.map(f => f.id === updatedFile.id ? updatedFile : f));
        break;
      case 'outputFiles':
        setOutputFiles(prev => prev.map(f => f.id === updatedFile.id ? updatedFile : f));
        break;
      case 'inputExample':
        setInputExampleFiles(prev => prev.map(f => f.id === updatedFile.id ? updatedFile : f));
        break;
      case 'outputExample':
        setOutputExampleFiles(prev => prev.map(f => f.id === updatedFile.id ? updatedFile : f));
        break;
      default:
        console.warn(`Unknown boxId in handleFileUpdate: ${boxId}`);
    }
  }, []);

  const handleFileClick = (event: React.MouseEvent<HTMLDivElement>, file: FileData, boxId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      file: file,
      boxId: boxId,
    });
  };

  const closeActionMenu = useCallback(() => {
    setActionMenu(prev => ({ ...prev, visible: false, file: null, boxId: null }));
  }, []);

  useEffect(() => {
    window.addEventListener('click', closeActionMenu);
    return () => {
      window.removeEventListener('click', closeActionMenu);
    };
  }, [closeActionMenu]);

  const handleMenuAction = (action: string, file: FileData, boxId: string) => {
    switch (action) {
      case 'Remove':
        removeFile(file.id, boxId);
        break;
      case 'Copy to Example Input':
        handleFilesAdd('inputExample', [{ ...file }]);
        break;
      case 'Copy to Example Output':
        handleFilesAdd('outputExample', [{ ...file }]);
        break;
      case 'Open File':
      case 'Edit Marks/Annotations':
        setPreviewFile(file);
        setPreviewBoxId(boxId);
        break;
      case 'View Details':
        alert(`File: ${file.name}\nType: ${file.type}\nSize: ${file.size ? (file.size / 1024).toFixed(2) + ' KB' : 'Unknown'}`);
        break;
      default:
        break;
    }
  };

  const handleNextClick = async () => {
    if (inputFiles.length === 0 && outputFiles.length === 0) {
      alert("Please add input or output files first.");
      return;
    }

    try {
      // Get current user ID from Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        alert("Please sign in to continue.");
        return;
      }

      // Show loading state
      setProcessingStatus('Uploading files to storage...');

      // Upload input files to data/input/ and output files to data/output/
      const uploadPromises: Promise<{ success: boolean; fileName: string; error?: any }>[] = [];
      
      // Upload input files to data/input/
      for (const fileData of inputFiles) {
        uploadPromises.push(
          (async () => {
            try {
              // Convert FileData to File
              const file = await fileDataToFile(fileData);
              
              // Upload to S3 temp_workspace/data/input/ folder
              // Use "temp" as project name since actual project name isn't set yet
              const storagePath = await s3UploadService.uploadFile({
                projectName: 'temp',
                fileName: `input/${fileData.name}`,
                file: file,
                fileType: 'data',
              });
              
              console.log(`[UPLOAD] Successfully uploaded ${fileData.name} to ${storagePath}`);
              return { success: true, fileName: fileData.name };
            } catch (error) {
              console.error(`[UPLOAD] Failed to upload ${fileData.name}:`, error);
              return { success: false, fileName: fileData.name, error };
            }
          })()
        );
      }
      
      // Upload output files to data/output/
      for (const fileData of outputFiles) {
        uploadPromises.push(
          (async () => {
            try {
              // Convert FileData to File
              const file = await fileDataToFile(fileData);
              
              // Upload to S3 temp_workspace/data/output/ folder
              const storagePath = await s3UploadService.uploadFile({
                projectName: 'temp',
                fileName: `output/${fileData.name}`,
                file: file,
                fileType: 'data',
              });
              
              console.log(`[UPLOAD] Successfully uploaded ${fileData.name} to ${storagePath}`);
              return { success: true, fileName: fileData.name };
            } catch (error) {
              console.error(`[UPLOAD] Failed to upload ${fileData.name}:`, error);
              return { success: false, fileName: fileData.name, error };
            }
          })()
        );
      }

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);
      
      // Check for failures
      const failures = results.filter(r => !r.success);
      if (failures.length > 0) {
        const failedNames = failures.map(f => f.fileName).join(', ');
        alert(`Failed to upload some files: ${failedNames}. Please try again.`);
        setProcessingStatus('');
        return;
      }

      // All uploads successful, proceed to next stage
      setProcessingStatus('');
      setStage('exampleSelection');
    } catch (error) {
      console.error('[UPLOAD] Error uploading files:', error);
      alert(`Error uploading files: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
      setProcessingStatus('');
    }
  };

  const handleTrainClick = () => {
    console.log('[TRAIN CLICK] Button clicked');
    if (inputExampleFiles.length === 0 || outputExampleFiles.length === 0) {
      alert("Please provide at least one input and output example.");
      return;
    }

    console.log('[TRAIN CLICK] isConnected:', isConnected);
    if (!isConnected) {
      alert("Not connected to the WebSocket server. Please wait for the connection or refresh the page.");
      return;
    }

    // Open the configuration window
    setStage('config');
  };

  const handleStartTraining = (config: TrainingConfig) => {
    console.log('[START TRAINING] Starting with config:', config);
    setTrainingConfig(config);

    // For inputFiles and outputFiles, only send metadata (no content) to avoid huge payloads
    // Only example files need full content for GPT analysis
    const dataToSend: BoxData = {
      inputFiles: inputFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size || 0,
        content: null, // Don't send content for full file list - too large!
        annotations: f.annotations ? JSON.stringify(f.annotations) : null,
        marks: f.marks ? JSON.stringify(f.marks) : null
      })),
      outputFiles: outputFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size || 0,
        content: null, // Don't send content for full file list - too large!
        annotations: f.annotations ? JSON.stringify(f.annotations) : null,
        marks: f.marks ? JSON.stringify(f.marks) : null
      })),
      inputExamples: inputExampleFiles.map(f => fileDataToFileInfo(f)), // Examples need full content
      outputExamples: outputExampleFiles.map(f => fileDataToFileInfo(f)), // Examples need full content
      prompt: config.description || description || "Transform the input files to match the output examples.",
      useGptCache: true, // Always use cached GPT results
      useModelCache: true, // Always use cached model code
      modelName: config.modelName, // Pass model name to backend
      gpuType: config.gpuType // Pass GPU type to backend
    };

    console.log('[START TRAINING] Calling processBoxData with:', {
      inputFiles: dataToSend.inputFiles.length,
      outputFiles: dataToSend.outputFiles.length,
      inputExamples: dataToSend.inputExamples.length,
      outputExamples: dataToSend.outputExamples.length,
      modelName: config.modelName,
      gpuType: config.gpuType
    });

    setProcessingStatus('Processing...');
    processBoxData(dataToSend);
    console.log('[START TRAINING] processBoxData called, setting stage to trainingInProgress');
    setStage('trainingInProgress');
    setProcessingStatus('');
  };

  const handleConfigCancel = () => {
    setStage('exampleSelection');
  };

  const trainingStartedRef = React.useRef(false);

  useEffect(() => {
    const handleBoxDataResponse = (data: any) => {
      if (data.status === 'success') {
        setProcessingStatus('Data processed successfully! Starting training...');
        // Start training through the context (only once)
        if (stage === 'trainingInProgress' && !trainingStartedRef.current) {
          trainingStartedRef.current = true;
          console.log('[TRAIN CLICK] Starting training from boxDataResponse');
          // CRITICAL: Get model name from trainingConfig first (source of truth), then from response
          const modelName = trainingConfig?.modelName || data.data?.model_name || data.data?.modelName;
          console.log('[TRAIN CLICK] Model name sources:', {
            fromTrainingConfig: trainingConfig?.modelName,
            fromResponseData: data.data?.model_name || data.data?.modelName,
            finalModelName: modelName
          });
          if (!modelName) {
            console.error('[TRAIN CLICK] ERROR: No model name found!');
          }
          startTraining(modelName);
        }
      } else {
        setProcessingStatus(`Error: ${data.message || 'Unknown error'}`);
        setStage('exampleSelection');
        trainingStartedRef.current = false;
      }
    };

    const handleProjectExists = (data: any) => {
      console.log('[PROJECT EXISTS]', data);
      setOverwriteDialog({
        show: true,
        projectName: data.project_name || 'Unknown Project',
        message: data.message || 'Project already exists.',
        originalData: data.originalData || null,
      });
    };

    const handleTrainingCancelled = (data: any) => {
      console.log('[TRAINING CANCELLED]', data);
      setOverwriteDialog({
        show: false,
        projectName: '',
        message: '',
        originalData: null,
      });
      setProcessingStatus('Training cancelled.');
      setStage('exampleSelection');
    };

    const handleError = (data: any) => {
      console.error('Received error event:', data);
      setProcessingStatus(data.message || 'An unknown error occurred.');
      setStage('exampleSelection');
      trainingStartedRef.current = false;
    };

    socketService.on('boxDataResponse', handleBoxDataResponse);
    socketService.on('projectExists', handleProjectExists);
    socketService.on('trainingCancelled', handleTrainingCancelled);
    socketService.on('error', handleError);

    return () => {
      socketService.off('boxDataResponse', handleBoxDataResponse);
      socketService.off('projectExists', handleProjectExists);
      socketService.off('trainingCancelled', handleTrainingCancelled);
      socketService.off('error', handleError);
    };
  }, [stage, startTraining]);

  const handleOverwriteConfirm = () => {
    if (overwriteDialog.originalData) {
      socketService.confirmOverwrite({
        overwrite: true,
        modelName: overwriteDialog.projectName,
        originalData: overwriteDialog.originalData,
      });
    }
    setOverwriteDialog({
      show: false,
      projectName: '',
      message: '',
      originalData: null,
    });
  };

  const handleOverwriteCancel = () => {
    if (overwriteDialog.originalData) {
      socketService.confirmOverwrite({
        overwrite: false,
        modelName: overwriteDialog.projectName,
        originalData: overwriteDialog.originalData,
      });
    }
    setOverwriteDialog({
      show: false,
      projectName: '',
      message: '',
      originalData: null,
    });
  };

  const handleContinueWithOld = () => {
    if (overwriteDialog.originalData) {
      socketService.confirmOverwrite({
        overwrite: false,
        continueWithOld: true,
        modelName: overwriteDialog.projectName,
        originalData: overwriteDialog.originalData,
      });
    }
    setOverwriteDialog({
      show: false,
      projectName: '',
      message: '',
      originalData: null,
    });
  };

  // Reset training started flag when stage changes away from training
  useEffect(() => {
    if (stage !== 'trainingInProgress') {
      trainingStartedRef.current = false;
    }
  }, [stage]);

  const handleLoadTestFiles = async () => {
    try {
      setProcessingStatus('Loading test files...');
      const { inputFiles, outputFiles, inputExampleFiles, outputExampleFiles } = await loadTestFiles();

      setInputFiles(inputFiles);
      setOutputFiles(outputFiles);
      setInputExampleFiles(inputExampleFiles);
      setOutputExampleFiles(outputExampleFiles);
      setStage('exampleSelection');
      setProcessingStatus('All files loaded!');
      setTimeout(() => setProcessingStatus(''), 5000);
    } catch (error) {
      console.error('Error loading test files:', error);
      setProcessingStatus('Error loading files');
      setTimeout(() => setProcessingStatus(''), 3000);
    }
  };

  const handleLoadMnistFiles = async () => {
    try {
      setProcessingStatus('Loading MNIST dataset...');
      const { inputFiles, outputFiles, inputExampleFiles, outputExampleFiles } = await loadMnistFiles();

      setInputFiles(inputFiles);
      setOutputFiles(outputFiles);
      setInputExampleFiles(inputExampleFiles);
      setOutputExampleFiles(outputExampleFiles);
      setStage('exampleSelection');
      setProcessingStatus(`MNIST loaded! ${inputFiles.length} images`);
      setTimeout(() => setProcessingStatus(''), 5000);
    } catch (error) {
      console.error('Error loading MNIST files:', error);
      setProcessingStatus('Error loading MNIST dataset');
      setTimeout(() => setProcessingStatus(''), 3000);
    }
  };

  return (
    <div className="training-app">
      <div className={`training-app-connection ${isConnected ? 'connected' : 'disconnected'}`}
           title={isConnected ? 'Connected to server' : 'Disconnected from server'}>
      </div>

      {processingStatus && (
        <div className="training-app-status">
          <div className="training-app-status-indicator"></div>
          <span>{processingStatus}</span>
        </div>
      )}

      {stage !== 'config' && stage !== 'trainingInProgress' && (
        <div className="training-app-load-files">
          <button
            onClick={handleLoadTestFiles}
            className="training-app-load-btn"
            title="Load test files from testfiles directory"
          >
            {React.createElement(FaCloudDownloadAlt as React.ComponentType<any>)}
            <span>Load Test Files</span>
          </button>
          <button
            onClick={handleLoadMnistFiles}
            className="training-app-load-btn training-app-load-btn-mnist"
            title="Load MNIST handwritten digits dataset"
          >
            {React.createElement(FaCloudDownloadAlt as React.ComponentType<any>)}
            <span>Load MNIST</span>
          </button>
        </div>
      )}

      <div className="training-app-logo">
        {React.createElement(FaRobot as React.ComponentType<any>, { size: 150 })}
      </div>

      {stage === 'config' && (
        <TrainingConfigWindow
          onStartTraining={handleStartTraining}
          onCancel={handleConfigCancel}
          defaultModelName={trainingConfig?.modelName || ''}
        />
      )}
      <main className="training-app-main">
        <AnimatePresence initial={false}>
          {stage === 'trainingInProgress' ? (
            <motion.div
              key="trainingDisplay"
              className="training-app-stage"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <TrainingProgressDisplay />
            </motion.div>
          ) : (
            <>
              <motion.div
                key="row1"
                className="training-app-row"
                variants={rowVariants}
                initial="initial"
                animate={stage === 'exampleSelection' ? 'slideDown' : 'initial'}
                transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.6 }}
              >
                <FileBox
                  id="inputFiles"
                  title="Input Files"
                  files={inputFiles}
                  onFilesAdd={handleFilesAdd}
                  onFileClick={handleFileClick}
                  onFileUpdate={handleFileUpdate}
                  className="training-app-filebox"
                />
                <FileBox
                  id="outputFiles"
                  title="Output Files"
                  files={outputFiles}
                  onFilesAdd={handleFilesAdd}
                  onFileClick={handleFileClick}
                  onFileUpdate={handleFileUpdate}
                  className="training-app-filebox"
                />
              </motion.div>

              {stage === 'exampleSelection' && (
                <motion.div
                  key="row2"
                  className="training-app-row training-app-row-overlay"
                  variants={rowVariants}
                  initial="slideUp"
                  animate="enter"
                  exit="slideUp"
                  transition={{ type: 'spring', stiffness: 120, damping: 20, duration: 0.7, delay: 0.1 }}
                >
                  <FileBox
                    id="inputExample"
                    title="Input Example"
                    files={inputExampleFiles}
                    onFilesAdd={handleFilesAdd}
                    onFileClick={handleFileClick}
                    onFileUpdate={handleFileUpdate}
                    className="training-app-filebox"
                  />
                  <FileBox
                    id="outputExample"
                    title="Output Example"
                    files={outputExampleFiles}
                    onFilesAdd={handleFilesAdd}
                    onFileClick={handleFileClick}
                    onFileUpdate={handleFileUpdate}
                    className="training-app-filebox"
                  />
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>

        {stage !== 'config' && (
          <div className="training-app-buttons">
            {stage === 'initial' && (
              <motion.button
                key="nextButton"
                onClick={handleNextClick}
                className="training-app-btn training-app-btn-primary"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Next →
              </motion.button>
            )}
            {stage === 'exampleSelection' && (
              <motion.button
                key="trainButton"
                onClick={handleTrainClick}
                className="training-app-btn training-app-btn-success"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: 'spring', delay: 0.5 }}
              >
                Train Model
              </motion.button>
            )}
          </div>
        )}
      </main>

      <ActionMenu
        menuState={actionMenu}
        onClose={closeActionMenu}
        onAction={handleMenuAction}
      />
      {previewFile && (
        <EnhancedFilePreview
          file={previewFile}
          onFileUpdate={(updatedFile) => {
            if (previewBoxId) {
              handleFileUpdate(previewBoxId, updatedFile);
            }
          }}
          onClose={() => {
            setPreviewFile(null);
            setPreviewBoxId(null);
          }}
          isOpen={!!previewFile}
        />
      )}
      {overwriteDialog.show && (
        <OverwriteProjectDialog
          projectName={overwriteDialog.projectName}
          message={overwriteDialog.message}
          onConfirm={handleOverwriteConfirm}
          onCancel={handleOverwriteCancel}
          onContinueWithOld={handleContinueWithOld}
        />
      )}
    </div>
  );
};

export default TrainingApp;
